import { Action, Dispatch, Middleware, MiddlewareAPI, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { Analytics } from '../data/Analytics';
import { Errors } from '../data/Errors';
import { Team } from '../types/GameplayTypes';
import { Log } from '../util/Logging';
import { gameConfigStore } from './GameConfigStore';
import { roundPoints } from './GameplayStateUtils';
import { GameplayState, gamePlayStore } from './GameplayStore';

type GameConfigState = ReturnType<typeof gameConfigStore.reducer>;
type GameplayStoreAPI = MiddlewareAPI<Dispatch, { gamePlay: GameplayState; gameConfig: GameConfigState; }>;

/**
 * Catch errors thrown from GameplayAction dispatch and report them. This allows for errors such as the Facilitator
 * attempting an answer during an invalid game state to be reported back to the user.
 */
export const gameplayActionMiddleware: Middleware<object, { gamePlay: GameplayState; gameConfig: GameConfigState; }> =
	(storeApi: GameplayStoreAPI) => (next) => (action) => {
		if (isGameplayAction(action)) {
			try {
				const previousState = storeApi.getState().gamePlay;
				logAction(previousState, action, 'start');
				const result = next(action);
				const newState = storeApi.getState().gamePlay;
				logAction(newState, action, 'end');

				trackPhaseTransition(storeApi, previousState.phase, newState.phase);

				return result;
			} catch (error) {
				Errors.report(`Gameplay error for action ${action.type} with payload ${JSON.stringify(action.payload)}`, error);
				const errorMessage = (error instanceof Error) ? error.message : String(error);
				storeApi.dispatch(gamePlayStore.actions.setError(errorMessage));
			}
		} else {
			return next(action);
		}
	};

function isGameplayAction(action: unknown): action is PayloadAction<unknown, `gameplay/${string}`> {
	return isPayloadAction(action) && action.type.startsWith('gamePlay/');
}

function isPayloadAction(action: unknown): action is PayloadAction {
	return (typeof action === 'object') && (action !== null) && ('type' in action);
}

function trackPhaseTransition(storeApi: GameplayStoreAPI, previousPhase: string, newPhase: string) {
	if ((previousPhase !== 'roundEnd') && (newPhase === 'roundEnd')) {
		trackRoundEnd(storeApi);
	} else if ((previousPhase !== 'gameEnd') && (newPhase === 'gameEnd')) {
		trackGameEnd(storeApi);
	}
}

function trackRoundEnd(storeApi: GameplayStoreAPI) {
	const state = storeApi.getState();
	const questionSet = gameConfigStore.selectors.questionSet(state);
	const question = gamePlayStore.selectors.currentQuestion(state);
	const scores = gamePlayStore.selectors.teamScores(state);
	const analytics = gamePlayStore.selectors.analytics(state);

	if (questionSet && question && (analytics.lastRoundWinner !== undefined)) {
		Analytics.track('round_end', {
			question_set_id: questionSet.id,
			question_set_name: questionSet.name,
			round_number: gamePlayStore.selectors.round(state) + 1,
			winner: (analytics.lastRoundWinner === Team.A) ? 'team_a' : 'team_b',
			points_awarded: gamePlayStore.selectors.roundPoints(state),
			score_team_a: scores[Team.A],
			score_team_b: scores[Team.B],
			answers_seen: question.answers.countWhere(a => a.revealed),
			total_answers: question.answers.length,
			incorrect_answers: gamePlayStore.selectors.strikes(state).strikes,
		});
	} else {
		Errors.report('Unable to track round_end', new Error(
			`Unable to track round_end. questionSetId=${questionSet?.id ?? 'none'}, questionId=${question?.id ?? 'none'}, lastRoundWinner=${analytics.lastRoundWinner ?? 'none'}`
		));
	}
}

function trackGameEnd(storeApi: GameplayStoreAPI) {
	const state = storeApi.getState();
	const questionSet = gameConfigStore.selectors.questionSet(state);
	const allQuestions = gamePlayStore.selectors.questions(state);
	const scores = gamePlayStore.selectors.teamScores(state);
	const analytics = gamePlayStore.selectors.analytics(state);

	if (questionSet && analytics.gameStartTime) {
		const totalAnswers = allQuestions.reduce((sum, q) => sum + q.answers.length, 0);
		const totalAttempts = analytics.totalCorrectAnswers + analytics.totalStrikes;

		const winner = (scores[Team.A] === scores[Team.B])
			? 'draw'
			: (scores[Team.A] > scores[Team.B]) ? 'team_a' : 'team_b';

		Analytics.track('game_end', {
			question_set_id: questionSet.id,
			question_set_name: questionSet.name,
			duration_minutes: dayjs().diff(dayjs(analytics.gameStartTime), 'minutes'),
			rounds_completed: gamePlayStore.selectors.round(state) + 1,
			score_team_a: scores[Team.A],
			score_team_b: scores[Team.B],
			winner,
			total_answers: totalAnswers,
			correct_answers: analytics.totalCorrectAnswers,
			incorrect_answers: analytics.totalStrikes,
			answer_rate_percent: (totalAttempts > 0) ? Math.round((analytics.totalCorrectAnswers / totalAttempts) * 100) : 0,
		});
	} else {
		Errors.report('Unable to track game_end', new Error(
			`Unable to track game_end. questionSetId=${questionSet?.id ?? 'none'}, gameStartTime=${analytics.gameStartTime ?? 'none'}`
		));
	}
}

function logAction<A extends Action>(state: Readonly<GameplayState>, action: A, startEnd: string) {
	const typeSuffix = action.type.split('/').last();
	const team = (state.controllingTeam === undefined)
		? 'none'
		: (state.controllingTeam === Team.A) ? 'A' : 'B';

	Log.game.debug(`Action ${typeSuffix} ${startEnd}: phase: ${state.phase}, control: ${team}`
		+ ` roundPoints: ${roundPoints(state)}, scores: [${state.teamScores[0]}, ${state.teamScores[1]}], strikes: ${state.strikes}`);
}
